import React from 'react';
import { Icon } from '../../../../elements/icon';
import useCopy from '../useCopy';
import { FOCUS_HERO } from '../assets';

const getClaimCount = (row) => row.res.hits.length;
const getFocusNameClass = (name) => (name.length > 26 ? 'long' : '');

const SymptomBody = ({ c, row }) => {
  const getName = (symptom) => (symptom.short || symptom.name)[c.lang];
  const symptomRows = row.res.bySymptom
    .filter((symptom) => symptom.hits.length)
    .sort((a, b) => b.hits.length - a.hits.length);

  return (
    <>
      <div className="fc-numrow">
        <b className="fc-num">{getClaimCount(row)}</b>
        <div className="fc-lab">
          <span>{c.text('PC_Focus_Symptoms')}</span>
        </div>
      </div>
      <ul className="fc-sym">
        {symptomRows.slice(0, symptomRows.length > 3 ? 2 : 3).map((symptom) => (
          <li key={symptom.code}>
            <span>{getName(symptom)}</span>
            <b>{symptom.hits.length}</b>
          </li>
        ))}
        {symptomRows.length > 3 && (
          <li className="more">
            <span>{c.text('PC_Focus_MoreSym', { m: symptomRows.length - 2 })}</span>
          </li>
        )}
      </ul>
    </>
  );
};

const IngredientChips = ({ c, row, focusName }) => (
  <>
    <p className="fc-ing-lab">{c.text('PC_Focus_Added', { focus: focusName })}</p>
    <div className="fc-chips">
      {row.ingredients.slice(0, 6).map((name) => (
        <span key={name}>{name}</span>
      ))}
      {row.ingredients.length > 6 && (
        <span className="m">{c.text('PC_Focus_MoreN', { m: row.ingredients.length - 6 })}</span>
      )}
    </div>
  </>
);

const MainClaimBody = ({ c, row, focusName }) => {
  const count = getClaimCount(row);

  if (row.thin) {
    return <IngredientChips c={c} row={row} focusName={focusName} />;
  }

  if (!count) return <p className="fc-none">{c.text('PC_Focus_None')}</p>;
  if (row.res.kind === 'symptoms') return <SymptomBody c={c} row={row} />;

  return (
    <div className="fc-numrow">
      <b className="fc-num">{count}</b>
      <div className="fc-lab">
        <span>
          {row.res.kind === 'related'
            ? c.text('PC_Focus_Related')
            : c.text(count === 1 ? 'PC_Focus_Claim1' : 'PC_Focus_ClaimN')}
        </span>
      </div>
    </div>
  );
};

const SmallFocusCard = ({ c, row, index, getFocusName }) => {
  const name = getFocusName(row.key);
  const count = getClaimCount(row);

  return (
    <div className="fc-card fc-small">
      <div className="fc-head">
        <i className="fc-rank">{index + 2}</i>
        <h3 className={getFocusNameClass(name)}>{name}</h3>
      </div>
      {row.thin ? (
        <p className="fc-ing2">
          <small>{c.text('PC_Focus_AddedS')}</small> {row.ingredients.slice(0, 4).join(' · ')}
          {row.ingredients.length > 4 && <i> {c.text('PC_Focus_MoreN', { m: row.ingredients.length - 4 })}</i>}
        </p>
      ) : count ? (
        <div className="fc-line">
          <b>{count}</b>
          <span>
            {row.res.kind === 'related' ? c.text('PC_Focus_Related') : c.text('PC_Focus_Short')}
          </span>
        </div>
      ) : (
        <p className="fc-none2">{c.text('PC_Focus_None')}</p>
      )}
    </div>
  );
};

const ExpertBadge = ({ c }) => (
  <div className="expert-badge">
    <div className="eb-seal">
      <span className="eb-l">
        <Icon raw name="productCarouselLaurel" />
      </span>
      <p>
        {c.text('PC_Focus_Badge1')}
        <br />
        {c.text('PC_Focus_Badge2')}
      </p>
      <span className="eb-l r">
        <Icon raw name="productCarouselLaurel" />
      </span>
    </div>
    <i className="eb-sep" />
    <p className="eb-text">{c.text('PC_Focus_BadgeText')}</p>
  </div>
);

const ViewFormulaCta = ({ c, onViewFormula }) => (
  <button type="button" className="fc-btn" onClick={onViewFormula}>
    {c.text('PC_Focus_Button')}
  </button>
);

const EditFormulaCta = ({ c, onEditFormula }) => (
  <div className="fc-edit">
    <p>{c.text('PC_Focus_NotHappy')}</p>
    <button type="button" className="pc-edit-link" onClick={onEditFormula}>
      {c.text('PC_Focus_EditLink')}
    </button>
  </div>
);

export const FocusSlide = ({ claims, onEditFormula, onViewFormula }) => {
  const c = useCopy();

  if (!claims?.rows?.length) return null;

  const [main, ...rest] = claims.rows;
  const titleKey = claims.rows.length === 1 ? 'PC_Focus_Title1' : claims.rows.length === 2 ? 'PC_Focus_Title2' : 'PC_Focus_Title';
  const getFocusName = (key) => c.text(`PC_Focus_Area_${key}`);
  const mainName = getFocusName(main.key);
  const mainFocusName = c.lang === 'de' ? mainName : mainName.toLowerCase();

  return (
    <div className="s-focus">
      <img className="pc-img fc-photo" alt="" src={FOCUS_HERO} />
      <div className="fc-col">
        <h2 className="fc-title">{c.rich(titleKey)}</h2>
        <p className="fc-sub">{c.text('PC_Focus_Sub')}</p>
        <div className="fc-card fc-main">
          <div className="fc-head">
            <i className="fc-rank">1</i>
            <h3 className={getFocusNameClass(mainName)}>{mainName}</h3>
          </div>
          <MainClaimBody c={c} row={main} focusName={mainFocusName} />
        </div>
        {rest.map((row, index) => (
          <SmallFocusCard key={row.key} c={c} row={row} index={index} getFocusName={getFocusName} />
        ))}
        <p className="fc-dosed">{c.text('PC_Focus_Dosed')}</p>
        <ExpertBadge c={c} />
      </div>
      <div className="fc-actions">
        <ViewFormulaCta c={c} onViewFormula={onViewFormula} />
        <EditFormulaCta c={c} onEditFormula={onEditFormula} />
      </div>
    </div>
  );
};
